import React, { useState } from 'react';
import { Database, PlayCircle, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DataToolsSettings — Settings → System → Data tools.
 *
 * Home for one-off, safe, admin-only data operations that historically
 * required a shell + a Python script. The first tool here is the
 * Stripe bonus backfill (2026-08-04): retroactively inserts the
 * `admin_adjustment / is_bonus=True` ledger rows that were missing
 * from historical Stripe recharges because the Stripe path was not
 * mirroring the PayPal flow.
 *
 * Design contract for this panel:
 *   1. Every operation MUST support dry-run first. The button labelled
 *      "Dry run" hits the backend with `dry_run: true` and displays
 *      exactly what WOULD happen — no writes, no user balance changes.
 *   2. Only after dry-run succeeds does the "Run for real" button
 *      unlock. This makes accidental double-clicks impossible.
 *   3. Every response includes counters + a 10-row sample so the
 *      operator can eyeball who is affected before committing.
 */
export default function DataToolsSettings({ token }) {
  const [emailFilter, setEmailFilter] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [busy, setBusy] = useState(null); // 'dry' | 'run' | null
  const [error, setError] = useState('');

  const callBackfill = async (dryRun) => {
    setError('');
    setBusy(dryRun ? 'dry' : 'run');
    try {
      const r = await fetch(`${API_URL}/api/admin/tools/backfill-stripe-bonus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dry_run: dryRun,
          user_email: emailFilter.trim() || undefined,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      if (dryRun) {
        setDryRunResult(j);
        setRunResult(null);
      } else {
        setRunResult(j);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(null);
    }
  };

  const canRunForReal = !!dryRunResult && dryRunResult.would_insert > 0 && !runResult;

  return (
    <div className="space-y-5" data-testid="data-tools-settings">
      {/* ── Stripe bonus backfill ─────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Database className="w-5 h-5 text-slate-700" />
          <h2 className="text-lg font-semibold">Backfill Stripe bonus rows</h2>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Historical Stripe recharges are missing their <code className="bg-slate-100 px-1 rounded text-xs">admin_adjustment / is_bonus</code> companion row.
          Customer balances are already correct — this only inserts the visual
          ledger row so bonuses show up in the Transactions view and analytics.
        </p>
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 text-xs mb-4 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Balances are <b>not</b> touched. Only <code className="text-[11px]">transactions</code> rows are inserted.
            Idempotent — running twice inserts nothing the second time.
          </div>
        </div>

        {/* Optional user filter */}
        <label className="block text-xs font-medium text-slate-600 mb-1">Filter by user email (optional — leave blank to process all)</label>
        <input
          type="email"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          placeholder="e.g. contact@redlinetuners.com"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          data-testid="backfill-email-filter"
        />

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            onClick={() => callBackfill(true)}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="backfill-dry-run-btn"
          >
            {busy === 'dry' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Dry run (preview)
          </button>
          <button
            onClick={() => {
              if (!window.confirm(`Insert ${dryRunResult.would_insert} bonus rows (total €${dryRunResult.would_insert_total_eur.toFixed(2)})?\n\nBalances will NOT be touched. Idempotent — safe to retry if it fails.`)) return;
              callBackfill(false);
            }}
            disabled={!canRunForReal || busy !== null}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700 disabled:opacity-40"
            data-testid="backfill-run-btn"
          >
            {busy === 'run' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Run for real
          </button>
        </div>

        {error && (
          <div className="text-sm px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-800 mb-3">{error}</div>
        )}

        {dryRunResult && <ResultBlock result={dryRunResult} kind="dry" />}
        {runResult && <ResultBlock result={runResult} kind="real" />}
      </div>
    </div>
  );
}

function ResultBlock({ result, kind }) {
  const isDry = kind === 'dry';
  const label = isDry ? 'Dry run result' : 'Live run result';
  const insertCount = isDry ? result.would_insert : result.inserted;
  const totalEur = isDry ? result.would_insert_total_eur : undefined;

  return (
    <div className={`rounded-lg border p-4 text-sm ${isDry ? 'bg-slate-50 border-slate-200' : 'bg-emerald-50 border-emerald-200'}`}>
      <div className="font-semibold mb-2">{label}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-3">
        <Stat label="Scanned" value={result.scanned} />
        <Stat label="Already had bonus row" value={result.already_ok} />
        <Stat label={isDry ? 'Would insert' : 'Inserted'} value={insertCount} highlight />
        {totalEur !== undefined && (
          <Stat label="Sum (EUR)" value={`€${totalEur.toFixed(2)}`} highlight />
        )}
      </div>
      {result.sample && result.sample.length > 0 && (
        <div className="text-xs">
          <div className="font-medium text-slate-600 mb-1">Sample ({result.sample.length}):</div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100 text-slate-600 text-[11px]">
                <tr>
                  <th className="px-2 py-1 text-left">User</th>
                  <th className="px-2 py-1 text-right">Amount</th>
                  <th className="px-2 py-1 text-left">Description</th>
                  <th className="px-2 py-1 text-left">Original date</th>
                </tr>
              </thead>
              <tbody>
                {result.sample.map((s, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1 truncate max-w-[220px]" title={s.user_email}>{s.user_email}</td>
                    <td className="px-2 py-1 text-right font-mono">€{Number(s.amount).toFixed(2)}</td>
                    <td className="px-2 py-1 truncate max-w-[280px] text-slate-600" title={s.description}>{s.description}</td>
                    <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{(s.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-md px-2 py-1.5 border ${highlight ? 'bg-white border-slate-300' : 'bg-white border-slate-200'}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`font-semibold ${highlight ? 'text-slate-900' : 'text-slate-700'}`}>{value}</div>
    </div>
  );
}
